import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Check, Copy, Loader2 } from 'lucide-react'
import type { SuperAgentTokenStatus } from '@/lib/api/features/super-agent'
import {
  generateSuperAgentToken,
  getSuperAgentToken,
  revokeSuperAgentToken,
} from '@/lib/api/features/super-agent'
import { useAiAvailable } from '@/lib/ai/useAiAvailable'
import { useDateTimeFormatters } from '@/lib/i18n/useDateTimeFormatters'
import { useHasPermission } from '@/lib/hooks/useWorkspaceRole'
import { WORKSPACE_PERMISSIONS } from '@/lib/types/roles'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'
import { FEATURE_FLAGS } from '@/lib/constants/featureFlags'

type Props = {
  workspaceSlug: string
}

/**
 * Workspace-level Super Agent bot token (`mp_…`). This is the credential the desktop
 * orchestrator authenticates with to act as the Super Agent. It is minted here explicitly
 * (never as a side effect of enabling a project) and shown only once — regenerating revokes
 * the previous token. Gated on the workspace being AI-enabled + the Manage AI settings role.
 */
export function SuperAgentTokenSection({ workspaceSlug }: Props) {
  const { t } = useTranslation('settings')
  const { date: formatDate } = useDateTimeFormatters()
  const aiAvailable = useAiAvailable()
  const canManage = useHasPermission(WORKSPACE_PERMISSIONS.MANAGE_AI_SETTINGS)

  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [status, setStatus] = useState<SuperAgentTokenStatus>({ provisioned: false })
  const [revealed, setRevealed] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [confirmRegenerate, setConfirmRegenerate] = useState(false)

  useEffect(() => {
    // Object ref so the async closure reads the live value on unmount.
    const alive = { current: true }
    setLoading(true)
    void (async () => {
      try {
        const result = await getSuperAgentToken(workspaceSlug)
        if (alive.current) setStatus(result)
      } catch (error) {
        logger.logError(error as Error, LogCategory.API_ERROR, { context: 'super_agent_token_status' })
      } finally {
        if (alive.current) setLoading(false)
      }
    })()
    return () => {
      alive.current = false
    }
  }, [workspaceSlug])

  const disabled = !aiAvailable || !canManage || busy

  const doGenerate = async () => {
    setBusy(true)
    try {
      const result = await generateSuperAgentToken(workspaceSlug)
      setRevealed(result.token)
      setCopied(false)
      setStatus({ provisioned: true, lastFour: result.lastFour, createdOn: result.createdOn })
      toast.success(t('workspaceSettings.superAgentToken.toast.generated'))
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t('workspaceSettings.superAgentToken.toast.generateFailed'),
      )
    } finally {
      setBusy(false)
      setConfirmRegenerate(false)
    }
  }

  const doRevoke = async () => {
    setBusy(true)
    try {
      await revokeSuperAgentToken(workspaceSlug)
      setStatus({ provisioned: false })
      setRevealed(null)
      toast.success(t('workspaceSettings.superAgentToken.toast.revoked'))
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t('workspaceSettings.superAgentToken.toast.revokeFailed'),
      )
    } finally {
      setBusy(false)
    }
  }

  const copyToken = async () => {
    if (!revealed) return
    try {
      await navigator.clipboard.writeText(revealed)
      setCopied(true)
      toast.success(t('workspaceSettings.superAgentToken.toast.copied'))
    } catch (error) {
      logger.logError(error as Error, LogCategory.API_ERROR, { context: 'super_agent_token_copy' })
    }
  }

  const createdLabel =
    status.createdOn != null
      ? t('workspaceSettings.superAgentToken.created', {
          date: formatDate(new Date(status.createdOn)),
        })
      : null

  // While Super Agent is hidden, keep the section reachable ONLY when a token already
  // exists: an issued credential keeps working against the backend, so admins must still
  // be able to revoke it. Nothing is shown (and no token can be minted) otherwise.
  if (!FEATURE_FLAGS.ENABLE_SUPER_AGENT && (loading || !status.provisioned)) {
    return null
  }

  return (
    <section className="flex flex-col gap-2">
      <h2 className="px-0.5 text-[13px] font-semibold">{t('workspaceSettings.superAgentToken.title')}</h2>
      <p className="-mt-1 mb-1 px-0.5 text-xs text-muted-foreground">
        {t('workspaceSettings.superAgentToken.description')}
      </p>

      {!aiAvailable && (
        <Alert className="mb-4">
          <AlertDescription>{t('workspaceSettings.superAgentToken.aiRequired')}</AlertDescription>
        </Alert>
      )}

      <div className="rounded-[14px] border bg-card p-[18px]">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            {t('workspaceSettings.superAgentToken.loading')}
          </div>
        ) : revealed ? (
          // One-time reveal — the only moment the full secret is shown.
          <div className="space-y-3">
            <p className="text-sm font-medium">{t('workspaceSettings.superAgentToken.revealTitle')}</p>
            <div className="flex items-center gap-2">
              <Input readOnly value={revealed} className="font-mono text-xs" onFocus={(e) => e.target.select()} />
              <Button type="button" variant="outline" size="sm" onClick={() => void copyToken()}>
                {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
                {copied
                  ? t('workspaceSettings.superAgentToken.copied')
                  : t('workspaceSettings.superAgentToken.copy')}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('workspaceSettings.superAgentToken.revealHint')}
            </p>
            <Button type="button" size="sm" onClick={() => setRevealed(null)}>
              {t('workspaceSettings.superAgentToken.done')}
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <p className="text-sm">
                {status.provisioned && status.lastFour
                  ? t('workspaceSettings.superAgentToken.provisioned', { lastFour: status.lastFour })
                  : t('workspaceSettings.superAgentToken.notProvisioned')}
              </p>
              {status.provisioned && createdLabel && (
                <p className="text-xs text-muted-foreground">{createdLabel}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {status.provisioned ? (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={disabled}
                    onClick={() => void doRevoke()}
                  >
                    {t('workspaceSettings.superAgentToken.revoke')}
                  </Button>
                  {/* Regenerate MINTS a new credential, so it stays hidden while Super
                      Agent is off — only revoke remains for an existing token. */}
                  {FEATURE_FLAGS.ENABLE_SUPER_AGENT && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={disabled}
                      onClick={() => setConfirmRegenerate(true)}
                    >
                      {busy && <Loader2 className="size-4 animate-spin" />}
                      {t('workspaceSettings.superAgentToken.regenerate')}
                    </Button>
                  )}
                </>
              ) : (
                <Button type="button" size="sm" disabled={disabled} onClick={() => void doGenerate()}>
                  {busy && <Loader2 className="size-4 animate-spin" />}
                  {t('workspaceSettings.superAgentToken.generate')}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      <Dialog open={confirmRegenerate} onOpenChange={(next) => !busy && setConfirmRegenerate(next)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('workspaceSettings.superAgentToken.regenerateTitle')}</DialogTitle>
            <DialogDescription>
              {t('workspaceSettings.superAgentToken.regenerateDescription')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => setConfirmRegenerate(false)}
            >
              {t('workspaceSettings.superAgentToken.cancel')}
            </Button>
            <Button type="button" disabled={busy} onClick={() => void doGenerate()}>
              {busy && <Loader2 className="size-4 animate-spin" />}
              {t('workspaceSettings.superAgentToken.regenerateCta')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}
